import React, { useState, useEffect, useMemo } from 'react';
import apiService from '../services/ApiService';

function ChallanSearched() {
  const [searches, setSearches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchParams, setSearchParams] = useState({
    searchId: '',
    vehicleNumber: '',
    found: '',
    date: ''
  });

  const [selectedSearch, setSelectedSearch] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);

  const itemsPerPage = 10;

  useEffect(() => {
    fetchSearchedChallans();
  }, [currentPage, searchParams]);

  const fetchSearchedChallans = async () => {
    setLoading(true);
    try {
      const queryParams = {
        page: currentPage,
        limit: itemsPerPage,
        search_id: searchParams.searchId,
        vehicle_number: searchParams.vehicleNumber,
        found: searchParams.found,
        date: searchParams.date
      };
      const result = await apiService.get('/vehicles/admin/searched-challans', queryParams);
      setSearches(result.searchedChallans || []);
      setTotal(result.total || 0);
    } catch (err) {
      setError(err.message || 'Failed to fetch search history');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    const { name, value } = e.target;
    setSearchParams(prev => ({ ...prev, [name]: value }));
    setCurrentPage(1);
  };

  const totalPages = Math.ceil(total / itemsPerPage);

  if (loading && searches.length === 0) return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '100px' }}>
      <i className="fas fa-circle-notch fa-spin" style={{ fontSize: '30px', color: 'var(--primary)' }}></i>
    </div>
  );

  return (
    <div className="search-history-page">
      <div className="page-header" style={{ marginBottom: '32px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div>
          <h2 style={{ fontFamily: 'Outfit', fontSize: '24px', fontWeight: '800', margin: '0', color: 'var(--text-main)' }}>Challan Search Audits</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginTop: '4px' }}>Log of every challan search query across the platform.</p>
        </div>
      </div>

      <div className="admin-card" style={{ marginBottom: '32px', padding: '24px' }}>
        <div className="grid-responsive">
          <div className="col-3 col-lg-6 col-md-12">
            <label style={labelStyle}>INQUIRY ID</label>
            <input type="text" name="searchId" value={searchParams.searchId} onChange={handleSearchChange} placeholder="Search ID..." style={inputStyle} />
          </div>
          <div className="col-3 col-lg-6 col-md-12">
            <label style={labelStyle}>VEHICLE PLATE</label>
            <input type="text" name="vehicleNumber" value={searchParams.vehicleNumber} onChange={handleSearchChange} placeholder="MH12AB..." style={inputStyle} />
          </div>
          <div className="col-3 col-lg-6 col-md-12">
            <label style={labelStyle}>OUTCOME</label>
            <select name="found" value={searchParams.found} onChange={handleSearchChange} style={inputStyle}>
              <option value="">Full History</option>
              <option value="true">Successful Hits</option>
              <option value="false">No-Data Inquiries</option>
            </select>
          </div>
          <div className="col-3 col-lg-6 col-md-12">
            <label style={labelStyle}>TIMESTAMP</label>
            <input type="date" name="date" value={searchParams.date} onChange={handleSearchChange} style={inputStyle} />
          </div>
        </div>
      </div>

      <div className="admin-card" style={{ padding: '0', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--bg-main)', textAlign: 'left' }}>
                <th style={tableHeaderStyle}>LOG ID</th>
                <th style={tableHeaderStyle}>VEHICLE</th>
                <th style={tableHeaderStyle}>SEARCHED BY</th>
                <th style={tableHeaderStyle}>STATUS</th>
                <th style={tableHeaderStyle}>TIMESTAMP</th>
                <th style={{ ...tableHeaderStyle, textAlign: 'right' }}>ANALYSIS</th>
              </tr>
            </thead>
            <tbody>
              {searches.map(s => (
                <tr key={s._id} className="table-row-hover" style={{ borderBottom: '1px solid var(--border-light)' }}>
                  <td style={tableCellStyle}>
                    <span style={{ color: 'var(--text-dim)', fontSize: '12px', fontWeight: '800' }}>#{s.search_id}</span>
                  </td>
                  <td style={tableCellStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{ width: '40px', height: '40px', background: 'var(--bg-main)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--primary)', border: '1px solid var(--border-light)', fontSize: '14px', fontWeight: '800' }}>
                        {s.vehicle_number.substring(0, 2)}
                      </div>
                      <div>
                        <div style={{ fontWeight: '800', fontSize: '14px', color: 'var(--text-main)', letterSpacing: '0.5px' }}>{s.vehicle_number}</div>
                      </div>
                    </div>
                  </td>
                  <td style={tableCellStyle}>
                    {s.searched_by ? (
                      <div>
                        <div style={{ fontWeight: '800', fontSize: '13px', color: 'var(--text-main)' }}>{s.searched_by.name || 'User'}</div>
                        <div style={{ fontSize: '11px', color: 'var(--text-dim)' }}>{s.searched_by.phone_number}</div>
                      </div>
                    ) : (
                      <span style={{ fontSize: '12px', color: 'var(--text-dim)' }}>System API</span>
                    )}
                  </td>
                  <td style={tableCellStyle}>
                    <span className={`status-badge ${s.found ? 'status-completed' : 'status-rejected'}`} style={{ fontSize: '10px', padding: '4px 12px' }}>
                      {s.found ? 'RECORDS RETRIEVED' : 'NO RECORDS'}
                    </span>
                  </td>
                  <td style={tableCellStyle}>
                    <div style={{ fontWeight: '600', color: 'var(--text-main)' }}>{new Date(s.search_timestamp).toLocaleDateString('en-GB')}</div>
                    <div style={{ fontSize: '11px', color: 'var(--text-dim)' }}>{new Date(s.search_timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                  </td>
                  <td style={{ ...tableCellStyle, textAlign: 'right' }}>
                    <button onClick={() => setSelectedSearch(s)} className="btn-action-view">
                      <i className="fas fa-eye"></i>
                      View Summary
                    </button>
                  </td>
                </tr>
              ))}
              {searches.length === 0 && (
                <tr>
                  <td colSpan="6" style={{ padding: '80px 20px', textAlign: 'center' }}>
                    <i className="fas fa-search" style={{ fontSize: '40px', color: 'var(--border-light)', marginBottom: '16px', display: 'block' }}></i>
                    <div style={{ fontWeight: '700', color: 'var(--text-dim)' }}>No log entries found for this inquiry.</div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div style={{ padding: '24px 32px', display: 'flex', justifyContent: 'center', gap: '12px', background: 'var(--bg-main)', borderTop: '1px solid var(--border-light)' }}>
          <button className="btn-nav" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)} style={navButtonStyle(currentPage === 1)}>
            <i className="fas fa-arrow-left"></i>
          </button>
          <div style={{ display: 'flex', alignItems: 'center', padding: '0 16px', fontSize: '13px', fontWeight: '800', color: 'var(--text-main)' }}>PAGE {currentPage} / {totalPages || 1}</div>
          <button className="btn-nav" disabled={currentPage === totalPages || totalPages === 0} onClick={() => setCurrentPage(p => p + 1)} style={navButtonStyle(currentPage === totalPages || totalPages === 0)}>
            <i className="fas fa-arrow-right"></i>
          </button>
        </div>
      </div>

      {/* Info Modal */}
      {selectedSearch && (
        <div className="modal-overlay" onClick={() => setSelectedSearch(null)} style={{ backdropFilter: 'blur(10px)', background: 'rgba(0,0,0,0.6)' }}>
          <div className="modal-content admin-card" style={{ maxWidth: '600px', padding: '0', overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '24px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--bg-main)', borderBottom: '1px solid var(--border-light)' }}>
              <div>
                <div style={{ fontSize: '10px', fontWeight: '800', color: 'var(--primary)', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '4px' }}>Log Detail Analysis</div>
                <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '800', color: 'var(--text-main)' }}>Challan Summary</h3>
              </div>
              <button
                onClick={() => setSelectedSearch(null)}
                style={{ width: '40px', height: '40px', borderRadius: '12px', border: 'none', background: 'white', color: 'var(--text-dim)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', boxShadow: 'var(--shadow-sm)' }}
              >
                ×
              </button>
            </div>

            <div style={{ padding: '32px' }}>
              {selectedSearch.challan_summary ? (
                <div className="grid-responsive">
                  <section style={{ border: '1px solid var(--border-light)', borderRadius: '20px', padding: '24px', width: '100%' }}>
                    <div style={sectionHeaderStyle}><i className="fas fa-file-invoice"></i> Summary Details</div>
                    <div className="data-row" style={dataRowStyle}><span>TOTAL CHALLANS</span><strong>{selectedSearch.challan_summary.total}</strong></div>
                    <div className="data-row" style={dataRowStyle}><span>PENDING CHALLANS</span><strong>{selectedSearch.challan_summary.pending}</strong></div>
                    <div className="data-row" style={dataRowStyle}><span>PAID CHALLANS</span><strong>{selectedSearch.challan_summary.paid}</strong></div>
                    <div className="data-row" style={{ ...dataRowStyle, borderBottom: 'none' }}><span>TOTAL PENDING AMOUNT</span><strong style={{ color: 'var(--error)' }}>₹{selectedSearch.challan_summary.total_pending_amount}</strong></div>
                  </section>
                </div>
              ) : (
                <div style={{ padding: '60px', textAlign: 'center', background: 'var(--bg-main)', borderRadius: '24px', border: '2px dashed var(--border-light)' }}>
                  <i className="fas fa-search-minus" style={{ fontSize: '48px', color: 'var(--text-dim)', marginBottom: '16px' }}></i>
                  <h4 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '800' }}>No Summary Found</h4>
                  <p style={{ color: 'var(--text-dim)', fontSize: '14px', maxWidth: '300px', margin: '0 auto' }}>Detailed challan specifications were not successfully indexed for this inquiry.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <style>{`
        .table-row-hover:hover { background: #fcfdfe !important; }
        .btn-action-view { display: flex; alignItems: center; gap: 8px; margin-left: auto; padding: 8px 16px; border-radius: 10px; border: 1px solid var(--border-light); background: white; color: var(--text-main); font-size: 12px; font-weight: 800; cursor: pointer; transition: all 0.2s; }
        .btn-action-view:hover { background: var(--bg-main); border-color: var(--primary); color: var(--primary); transform: translateX(-4px); }
        .btn-nav:hover:not(:disabled) { transform: translateY(-2px); box-shadow: var(--shadow-md); color: var(--primary); }
        .modal-overlay { position: fixed; inset: 0; display: flex; align-items: center; justify-content: center; z-index: 1000; }
      `}</style>
    </div>
  );
}

const labelStyle = {
  display: 'block',
  fontSize: '10px',
  fontWeight: '800',
  color: 'var(--text-dim)',
  letterSpacing: '1px',
  marginBottom: '8px'
};

const inputStyle = {
  width: '100%',
  padding: '12px 16px',
  borderRadius: '12px',
  border: '1px solid var(--border-light)',
  fontSize: '14px',
  fontWeight: '600',
  background: '#fcfcfc',
  transition: 'all 0.2s',
  outline: 'none',
  boxSizing: 'border-box'
};

const tableHeaderStyle = { padding: '16px 24px', color: 'var(--text-dim)', fontSize: '11px', fontWeight: '800', letterSpacing: '1px' };
const tableCellStyle = { padding: '20px 24px', fontSize: '14px' };

const sectionHeaderStyle = {
  fontSize: '12px',
  fontWeight: '900',
  color: 'var(--text-main)',
  textTransform: 'uppercase',
  letterSpacing: '1px',
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  marginBottom: '20px'
};

const dataRowStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  padding: '12px 0',
  borderBottom: '1px solid var(--bg-main)',
  fontSize: '13px'
};

const navButtonStyle = (disabled) => ({
  width: '40px', height: '40px', borderRadius: '12px', border: '1px solid var(--border-light)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: disabled ? 'not-allowed' : 'pointer',
  background: 'white', color: disabled ? 'var(--border-light)' : 'var(--text-main)',
  transition: 'all 0.2s', boxShadow: 'var(--shadow-sm)'
});

export default ChallanSearched;
